import React, { useState } from "react";
import { KeyRound, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";
import { confirmAsync } from "@/lib/safeConfirm";
import { Card, CopyButton, CodeBlock, btnDark, fmtWhen } from "./atoms";

const SAMPLE = `{
  "generated_at": "2026-06-18T10:00:00Z",
  "count": 1,
  "trips": [{
    "id": "crt_4717641ceeec",
    "public_url": "https://trips.espiritutravel.com/curated/6e5e3161…",
    "name": "Eat Your Way Through Sicily",
    "tagline": "8 days · October 2026",
    "hero_image": "https://…/hero.jpg",
    "start_date": "2026-10-14", "end_date": "2026-10-22", "duration_days": 8,
    "cities": ["Palermo", "Taormina"],
    "capacity": 12, "seats_available": 3, "seats_committed": 9,
    "sold_out": false, "accepting_bookings": true, "status": "published",
    "price_single": 4200, "price_double": 3584, "price_from": 3584, "currency": "USD",
    "published_at": "2026-06-01T09:12:00Z", "updated_at": "2026-06-18T09:58:00Z"
  }]
}`;

export function HubApiCard({ settings, reload }) {
  const [freshKey, setFreshKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const listUrl = `${API_BASE}/hub/v1/curated-trips`;

  const rotate = async () => {
    if (settings.api_key_prefix) {
      const ok = await confirmAsync("Se generará una clave nueva y la anterior dejará de funcionar. La landing tendrá que actualizarla.", { destructive: true, confirmLabel: "Rotar clave" });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/settings/hub/api-key");
      setFreshKey(data.api_key);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar la clave"); }
    finally { setBusy(false); }
  };

  return (
    <Card kicker="Landing hub" title="API de lectura — lista de viajes" testid="hub-api-card">
      <p className="text-sm text-clay-700 -mt-2">
        Complemento para el hub: además del aviso por viaje, puede pedir aquí la <strong>lista completa</strong> de Curated Trips
        publicados (para descubrir viajes nuevos en su sincronización horaria). Solo salen viajes publicados o cerrados; nunca
        borradores, reservas ni datos de compradores.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <code className="text-[12px] bg-clay-100 px-2 py-1.5 border border-clay-200 break-all" data-testid="hub-list-url">GET {listUrl}</code>
        <CopyButton text={listUrl} testid="hub-copy-url"/>
      </div>
      <div className="text-[12px] text-clay-600">
        Filtros: <code>?status=published</code> (solo reservables) · <code>?status=live</code> (publicados + cerrados, por defecto) ·
        <code> /hub/v1/curated-trips/&#123;id&#125;</code> para uno solo.
      </div>

      <div className="border border-clay-200 bg-clay-50 p-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="smallcaps">Clave de API (cabecera <code>X-API-Key</code>)</div>
          {settings.api_key_prefix ? (
            <div className="text-sm mt-1" data-testid="hub-key-status">
              <span className="font-mono">{settings.api_key_prefix}…</span>
              <span className="text-clay-600"> · creada {fmtWhen(settings.api_key_created_at)} · último uso {settings.api_key_last_used_at ? fmtWhen(settings.api_key_last_used_at) : "nunca"}</span>
            </div>
          ) : (
            <div className="text-sm mt-1 text-clay-700" data-testid="hub-key-status">Aún no hay clave: la API responde 401 hasta que generes una.</div>
          )}
        </div>
        <button onClick={rotate} disabled={busy} className={btnDark} data-testid="hub-generate-key-btn">
          {busy ? <Loader2 size={12} className="animate-spin"/> : <KeyRound size={12}/>} {settings.api_key_prefix ? "Rotar clave" : "Generar clave"}
        </button>
      </div>
      {freshKey && (
        <div className="border border-amber-300 bg-amber-50 p-4" data-testid="hub-fresh-key">
          <div className="flex items-center gap-2 text-amber-900 text-[12px] font-medium"><AlertTriangle size={13}/> Copia la clave ahora: no se volverá a mostrar.</div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <code className="font-mono text-[12px] bg-white border border-amber-200 px-2 py-1.5 break-all" data-testid="hub-fresh-key-value">{freshKey}</code>
            <CopyButton text={freshKey} testid="hub-copy-key"/>
          </div>
        </div>
      )}

      <details className="text-sm">
        <summary className="cursor-pointer text-clay-800 hover:text-terracotta">Ejemplo de llamada y respuesta</summary>
        <div className="mt-3 space-y-3">
          <CodeBlock testid="hub-curl">{`curl "${listUrl}?status=published" \\\n  -H "X-API-Key: et_hub_TU_CLAVE"`}</CodeBlock>
          <CodeBlock>{SAMPLE}</CodeBlock>
          <p className="text-[12px] text-clay-600">
            Usa la clave desde el servidor de tu landing (o un proxy), no en JavaScript público. Si la landing es estática,
            programa una regeneración cada pocos minutos o usa el webhook de abajo para regenerar al instante.
          </p>
        </div>
      </details>
    </Card>
  );
}
